import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { canView as canViewPath, decodeAuthorization, hasPermission, hasScope, hasTagAccess, type AuthorizationState, type Permission } from '../auth/authorization';

interface AuthorizationContextValue {
    authorization: AuthorizationState | null;
    isLoading: boolean;
    can: (permissions: string[]) => boolean;
    canView: (view: string) => boolean;
    hasScope: (scope: string) => boolean;
    hasTagAccess: (tagId: string | number) => boolean;
    hydrate: (accessToken: string, permissionCatalog?: Permission[]) => void;
    clear: () => void;
}

const AuthorizationContext = createContext<AuthorizationContextValue | null>(null);

const readPermissionCatalog = (): Permission[] => {
    try {
        const storedCatalog = localStorage.getItem('all_permissions');
        return storedCatalog ? JSON.parse(storedCatalog) : [];
    } catch {
        return [];
    }
};

const AuthorizationProvider = ({ children }: { children: ReactNode }) => {
    const [authorization, setAuthorization] = useState<AuthorizationState | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const hydrate = (accessToken: string, permissionCatalog?: Permission[]) => {
        setIsLoading(true);

        try {
            setAuthorization(decodeAuthorization(accessToken, permissionCatalog ?? readPermissionCatalog()));
        } catch {
            setAuthorization(null);
        } finally {
            setIsLoading(false);
        }
    };

    const clear = () => {
        setAuthorization(null);
        setIsLoading(false);
    };

    useEffect(() => {
        const syncFromStorage = () => {
            const accessToken = localStorage.getItem('access_token');
            if (accessToken) {
                hydrate(accessToken);
            } else {
                clear();
            }
        };

        syncFromStorage();
        window.addEventListener('authorization:token-updated', syncFromStorage);

        return () => {
            window.removeEventListener('authorization:token-updated', syncFromStorage);
        };
    }, []);

    const value: AuthorizationContextValue = {
        authorization,
        isLoading,
        can: (permissions) => hasPermission(authorization, permissions),
        canView: (view) => canViewPath(authorization, view),
        hasScope: (scope) => hasScope(authorization, scope),
        hasTagAccess: (tagId) => hasTagAccess(authorization, tagId),
        hydrate,
        clear,
    };

    return <AuthorizationContext.Provider value={value}>{children}</AuthorizationContext.Provider>;
};

const useAuthorization = () => {
    const context = useContext(AuthorizationContext);
    if (!context) {
        throw new Error('useAuthorization debe utilizarse dentro de AuthorizationProvider');
    }

    return context;
};

export { AuthorizationProvider, useAuthorization };
